
import { useProfileQuery } from "./profile/useProfileQuery";
import { useProfileUpdate } from "./profile/useProfileUpdate";
import { Profile } from "./profile/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type { Profile };  // Export the Profile type

const defaultProfile: Profile = {
  id: "",
  username: "",
  avatar_url: null,
  role: "socio",
  first_name: "",
  last_name: "",
  banned: false,
  gym_id: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  birthdate: null,
  dni: null,
  phone: null,
  address: null,
  emergency_phone: null,
  health_insurance: null
};

export const useProfile = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const queryClient = useQueryClient();

  // Verificar estado de autenticación
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const { data: profile, isLoading, error, refetch } = useProfileQuery();

  console.log("useProfile hook state:", { 
    profile: profile,
    isLoading, 
    isAuthenticated,
    error: error ? {
      message: error.message,
      stack: error instanceof Error ? error.stack : "No stack trace available"
    } : null,
    birthdate: profile?.birthdate,
    hasRealProfile: !!profile
  });

  const { mutate: updateProfile } = useProfileUpdate();

  // Función wrapper para actualizar el perfil y luego refrescar los datos
  const handleUpdateProfile = async (updates: Partial<Profile>) => {
    return new Promise<void>((resolve, reject) => {
      updateProfile(updates, {
        onSuccess: () => {
          console.log("Perfil actualizado con éxito, refrescando datos");
          
          // Invalidar la caché para forzar una nueva carga
          queryClient.invalidateQueries({ queryKey: ["profile"] });
          
          // Refrescar manualmente los datos
          refetch()
            .then((result) => {
              // Verificar explícitamente que los datos actualizados están disponibles
              if (result.data) {
                console.log("Datos actualizados del perfil:", result.data);
                // Actualizar manualmente la caché con los nuevos datos
                queryClient.setQueryData(["profile"], result.data);
                resolve();
              } else {
                console.warn("La actualización del perfil fue exitosa, pero no se recibieron datos");
                resolve(); // Resolver de todas formas para no bloquear el flujo
              }
            })
            .catch((refreshError) => {
              console.error("Error al refrescar datos del perfil:", refreshError);
              reject(refreshError);
            });
        },
        onError: (error) => {
          console.error("Error al actualizar perfil:", error);
          reject(error);
        }
      });
    });
  };

  // Si no está autenticado, devolver un perfil por defecto sin causar errores
  if (isAuthenticated === false) {
    return {
      profile: defaultProfile,
      isLoading: false,
      error: null,
      updateProfile: handleUpdateProfile,
      refetch
    };
  }

  // Solo devolver el perfil real cuando exista, null mientras carga
  return {
    profile: profile,
    isLoading: isLoading || isAuthenticated === null || !profile,
    error,
    updateProfile: handleUpdateProfile,
    refetch
  };
};
